import React from 'react'
import {
  Table,
  Section,
  SectionHeader,
  SectionBody,
} from '@apexinternal/ui-components'
import { FaArrowRight } from 'react-icons/fa'
import { Box, BoxProps, Link, Icon } from '@chakra-ui/react'
import { isEmpty, uniqueId } from 'lodash'
import { Balances } from 'types/Balances'
import { numberToDollars } from 'utils'
import { useAccountDetails } from 'hooks'
import { AccountTypeEnum } from 'types/AccountTypeEnum'
import { RoutesEnum } from 'types/RoutesEnum'

export interface BalancesTableProps extends BoxProps {
  balances?: Balances
}

const columns = [
  {
    key: 'type',
    label: 'Type',
    recordField: 'type',
    isSortable: false,
    cellRenderer: ({ value }: { value: any }) => (
      <Box fontWeight="500">{value}</Box>
    ),
  },
  {
    key: 'trade',
    label: 'Trade',
    recordField: 'trade',
    isSortable: false,
    isNumeric: true,
    width: '25%',
    cellRenderer: ({ value }: { value: any }) => (
      <Box>{value ? numberToDollars(value) : ''}</Box>
    ),
  },
  {
    key: 'settled',
    label: 'Settled',
    recordField: 'settled',
    isSortable: false,
    isNumeric: true,
    width: '25%',
    cellRenderer: ({ value }: { value: any }) => (
      <Box>{value ? numberToDollars(value) : ''}</Box>
    ),
  },
]

const getTableData = (balances?: Balances) => {
  const sums = {
    trade: 0,
    settled: 0,
  }

  const balancesRows = balances
    ? Object.entries(balances).flatMap(
        ([_type, [{ trade = 0, settled = 0 }]]) => {
          if (trade === 0 && settled === 0) return []

          const type = _type as keyof typeof balances
          sums.trade += trade
          sums.settled += settled
          return [
            {
              key: uniqueId(),
              type: TypeDisplay[type] || type,
              trade,
              settled,
            },
          ]
        }
      )
    : []

  if (isEmpty(balances) || balancesRows.length === 0)
    return [
      {
        key: 1,
        type: 'There are no balances to display.',
        trade: '',
        settled: '',
      },
    ]

  return [
    ...balancesRows,
    {
      key: uniqueId(),
      type: 'Totals',
      trade: sums.trade,
      settled: sums.settled,
    },
  ]
}

const BalancesTable = ({ balances, ...rest }: BalancesTableProps) => {
  const {
    accountDetailsState: { accountDetails },
  } = useAccountDetails()
  const accountNumber = accountDetails?.accountNumber
  const isCashAccount = accountDetails?.accountType === AccountTypeEnum.Cash
  return (
    <Section {...rest}>
      <SectionHeader
        tools={
          accountNumber &&
          isCashAccount && (
            <Link
              href={`/${RoutesEnum.Accounts}/${accountNumber}/balances`}
              sx={{
                ':hover': {
                  color: 'apex.white',
                },
                color: 'apex.white',
                fontWeight: 'regular',
              }}
            >
              Balance Details <Icon as={FaArrowRight} color="apex.white" />
            </Link>
          )
        }
      >
        Balances
      </SectionHeader>
      <SectionBody>
        <Table
          // ignoring error due to using string as width in columns, which is a valid value
          // typing in ui-components needs to be corrected
          // @ts-ignore
          columns={columns}
          data={getTableData(balances)}
          defaultSort={{
            ascending: true,
          }}
          sx={{ 'tr:last-of-type td': { fontWeight: '500' } }}
        />
      </SectionBody>
    </Section>
  )
}

export default BalancesTable

const TypeDisplay: { [key in keyof Balances]: string } = {
  brokerDealer: 'Broker Dealer',
  cash: 'Cash',
  creditInterest: 'Credit Interest',
  dividendInterestPayable: 'Dividend Interest Payable',
  dividendInterestReceivable: 'Dividend Interest Receivable',
  funding: 'Funding',
  futures: 'Futures',
  margin: 'Margin',
  moneyMarket: 'Money Market',
  nonConvertibleBonds: 'Nonconvertible Bonds',
  other: 'Other',
  restricted: 'Restricted',
  short: 'Short',
  specialSubscription: 'Special Subscription',
  tefraEscrow: 'TEFRA Escrow',
}
